import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useCommunity } from '@/contexts/CommunityContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { 
  Table, TableBody, TableCell, TableHead, 
  TableHeader, TableRow 
} from '@/components/ui/table';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import { Search, Mail, AlertTriangle, Calendar, Eye } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useIsMobile } from '@/hooks/use-mobile';
import { AdminPagination } from '@/components/admin/AdminPagination';
import { format } from 'date-fns';
import { EmailViewSheet } from '@/components/admin/EmailViewSheet';

interface EmailEvent {
  id: string;
  template_type: string;
  user_id: string;
  user_email: string;
  community_id: string;
  event_data: {
    email_action_type?: string;
    email_id?: string;
    sender_email?: string;
    template_variables?: Record<string, any>;
    error?: string;
    [key: string]: any;
  };
  email_sent: boolean;
  processed_at: string;
  created_at: string;
  processed_subject?: string;
  processed_html?: string;
  sender_email?: string;
  user_profile?: {
    firstname: string;
    lastname: string;
  };
}

const CommunicationManagement = () => {
  const { currentCommunity } = useCommunity();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const isMobile = useIsMobile();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, pageSize]);

  const { data: eventsResponse, isLoading, error } = useQuery({
    queryKey: ['admin', 'email-events', currentCommunity.id, debouncedSearchTerm, currentPage, pageSize],
    queryFn: async () => {
      let query = supabase
        .from('email_events')
        .select(`
          *,
          user_profile:user_profiles!email_events_user_id_fkey(
            firstname,
            lastname
          )
        `, { count: 'exact' })
        .eq('community_id', currentCommunity.id)
        .order('created_at', { ascending: false });

      // Apply search filter
      if (debouncedSearchTerm) {
        query = query.or(`event_data->template_variables->>business_name.ilike.%${debouncedSearchTerm}%,event_data->template_variables->>user_name.ilike.%${debouncedSearchTerm}%,event_data->template_variables->>business_owner_name.ilike.%${debouncedSearchTerm}%`);
      }

      // Apply pagination
      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;
      
      query = query.range(from, to);
        
      const { data, error, count } = await query;
      if (error) throw error;
      
      return {
        events: data || [],
        totalCount: count || 0,
        totalPages: Math.ceil((count || 0) / pageSize)
      };
    },
    enabled: !!currentCommunity.id
  });

  const events = eventsResponse?.events || [];
  const totalCount = eventsResponse?.totalCount || 0;
  const totalPages = eventsResponse?.totalPages || 0;

  const handleOpenEmail = (eventId: string) => {
    searchParams.set('event_id', eventId);
    setSearchParams(searchParams);
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM d, yyyy HH:mm');
  };

  const getTemplateLabel = (templateType: string): string => {
    const labels: Record<string, string> = {
      'email_confirmation': 'Email Confirmation',
      'password_reset': 'Password Reset',
      'welcome': 'Welcome',
      'business_registration': 'Business Registration',
      'business_approval': 'Business Approval',
      'business_listed': 'Business Listed',
      'pending_subscription_business_listed': 'Pending Subscription Business Listed',
      'review_notification': 'Review Notification',
      'offline_notification': 'Offline Notification',
      'job_proposal_notification': 'Job Proposal',
      'proposal_response_notification': 'Proposal Response',
      'admin_business_pending_approval': 'Admin: Pending Approval'
    };
    return labels[templateType] || templateType;
  };

  const getStatusBadge = (emailSent: boolean) => {
    return emailSent ? (
      <Badge variant="default" className="bg-green-100 text-green-700 hover:bg-green-100">
        Sent
      </Badge>
    ) : (
      <Badge variant="destructive">
        Failed
      </Badge>
    );
  };


  return (
    <div className="space-y-4 sm:space-y-6 lg:space-y-8 animate-fade-in p-1 sm:p-0">
      <div className="relative px-1">
        <h1 className="admin-section-title mb-2 text-2xl sm:text-3xl">Communication Management</h1>
        <p className="text-muted-foreground/80 text-base sm:text-lg">
          View all email communications sent to community members
        </p>
      </div>
      
      <Card className="admin-card admin-card-stats">
        <CardHeader className="pb-4 sm:pb-6">
          <CardTitle className="text-lg sm:text-xl font-bold text-slate-800">Email Communications</CardTitle>
          <CardDescription className="text-muted-foreground/80 mt-2 text-sm sm:text-base">
            Track all emails sent to members of {currentCommunity.name} community
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by email, business name, or user name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
          
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="flex space-x-2">
                <div className="h-3 w-3 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse"></div>
                <div className="h-3 w-3 bg-gradient-to-r from-accent to-primary rounded-full animate-pulse animation-delay-200"></div>
                <div className="h-3 w-3 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse animation-delay-400"></div>
              </div>
            </div>
          ) : error ? (
            <div className="text-destructive p-4 rounded-md bg-destructive/10">
              <div className="flex items-center">
                <AlertTriangle className="h-5 w-5 mr-2" />
                <span>Error loading email events</span>
              </div>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              {debouncedSearchTerm ? 'No email events match your search.' : 'No email events found.'}
            </div>
          ) : isMobile ? (
            // Mobile card view
            <div className="space-y-4">
              {events.map((event, index) => (
                <Card 
                  key={(event as any).id} 
                  className={`admin-card admin-card-action animate-fade-in stagger-${Math.min(index + 1, 5)} cursor-pointer`}
                  onClick={() => handleOpenEmail((event as any).id)}
                >
                  <CardContent className="p-4">
                    <div className="flex flex-col gap-3">
                      {/* Status Badge - Top Right */}
                      <div className="flex justify-end">
                        {getStatusBadge((event as any).email_sent)}
                      </div>
                      
                      {/* Subject Line - Most Important */}
                      <div className="space-y-1">
                        <div className="font-semibold text-slate-800 text-base leading-tight">
                          {event.processed_subject || 'No Subject'}
                        </div>
                        <div className="text-sm text-muted-foreground break-all">
                          {event.user_email}
                        </div>
                      </div>
                      
                      {/* Additional Details */}
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Mail className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                          <span className="text-muted-foreground/80">
                            {getTemplateLabel(event.template_type)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                          <span className="text-muted-foreground/80">
                            {formatDate(event.processed_at || event.created_at)}
                          </span>
                        </div>
                        {(event as any).event_data?.template_variables?.business_name && (
                          <div className="text-muted-foreground/70 flex items-center gap-2">
                            <div className="h-3 w-3 flex-shrink-0" />
                            <span>Business: {(event as any).event_data?.template_variables?.business_name}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex justify-end pt-3 border-t">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEmail((event as any).id);
                          }}
                          className="w-full sm:w-auto"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View Email
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            // Desktop table view
            <div className="rounded-xl border border-gray-200/60 overflow-hidden bg-white shadow-sm overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-gray-100 bg-gray-50/50">
                    <TableHead className="font-semibold text-slate-700 h-12 min-w-[300px]">Subject & Email</TableHead>
                    <TableHead className="font-semibold text-slate-700 min-w-[140px]">Template Type</TableHead>
                    <TableHead className="font-semibold text-slate-700 min-w-[120px]">Business</TableHead>
                    <TableHead className="font-semibold text-slate-700 min-w-[140px]">Date Sent</TableHead>
                    <TableHead className="font-semibold text-slate-700 min-w-[80px]">Status</TableHead>
                    <TableHead className="text-right font-semibold text-slate-700 min-w-[80px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map((event) => (
                    <TableRow 
                      key={(event as any).id}
                      className="cursor-pointer hover:bg-gray-50/80 border-b border-gray-100/50 transition-colors"
                      onClick={() => handleOpenEmail((event as any).id)}
                    >
                      <TableCell className="max-w-[300px]">
                        <div className="space-y-1">
                          <div className="font-semibold text-slate-800 leading-tight">
                            {(event as any).processed_subject || 'No Subject'}
                          </div>
                          <div className="text-sm text-muted-foreground break-all">
                            {(event as any).user_email}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[140px]">
                        <Badge variant="outline" className="font-normal text-xs">
                          {getTemplateLabel((event as any).template_type)}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[120px]">
                        <div className="truncate">
                          {(event as any).event_data?.template_variables?.business_name || '-'}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[140px]">
                        <div className="text-sm">
                          {formatDate((event as any).processed_at || (event as any).created_at)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge((event as any).email_sent)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="outline" 
                          size="icon" 
                          className="h-8 w-8"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEmail((event as any).id);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <AdminPagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
          />
        </CardContent>
      </Card>

      <EmailViewSheet communityId={currentCommunity.id} />
    </div>
  );
};

export default CommunicationManagement;